/**
 * ScanMini API 타입 및 Hooks
 * @MP/shared/api/hooks/scanMini에서 타입과 hooks를 모두 import할 수 있도록 함
 */

// 타입 re-export (실제 사용되는 타입만 명시적으로 export)
export {
  PresignedUrlRequestRecordingTypeEnum as ScanMiniRecordingType,
  PrescriptionSensorTypeEnum as ScanMiniSensorType,
} from '../generated/scan-mini';
export { RecordingDataControllerApi } from '../generated/scan-mini/api/recording-data-controller-api';
export { PrescriptionStatusEnum as ScanMiniPrescriptionStatus } from '../generated/scan-mini/model/prescription';
export type { Prescription as ScanMiniPrescription } from '../generated/scan-mini/model/prescription';

// Hooks 구현
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { API_CONFIG } from '../config/env';
import {
  AnalysisControllerApi,
  AnalysisResultDownloadUrls,
  GetRecordingDataPathsRecordingTypeEnum,
  Prescription,
  PrescriptionControllerApi,
  PrescriptionCreateDto,
  RecordingDataControllerApi,
  RecordingDataPaths,
} from '../generated/scan-mini';
import {
  QK_SCAN_MINI_ANALYSIS_FILES,
  QK_SCAN_MINI_ANALYSIS_RESULT,
  QK_SCAN_MINI_MY_PRESCRIPTION_LIST,
  QK_SCAN_MINI_PRESCRIPTION_LIST,
} from '../query-keys';

const prescriptionControllerApi = new PrescriptionControllerApi(undefined, API_CONFIG.SCAN_MINI_URL);
const recordingDataControllerApi = new RecordingDataControllerApi(undefined, API_CONFIG.SCAN_MINI_URL);
const analysisControllerApi = new AnalysisControllerApi(undefined, API_CONFIG.SCAN_MINI_URL);

// Prescription API hooks
export const useCreatePrescription = () => {
  const client = useQueryClient();
  return useMutation<Prescription, AxiosError, PrescriptionCreateDto>({
    mutationFn: (body) => prescriptionControllerApi.create(body).then((res) => res.data),
    onSuccess: (_, body) => {
      if (body.patientId) {
        client.invalidateQueries({ queryKey: [QK_SCAN_MINI_PRESCRIPTION_LIST, body.patientId] });
      }
    },
  });
};

// TODO: SCAN MINI API 배포 전이라 임시 처리
export const useFetchPrescriptions = (patientId?: string) => {
  return useQuery<Prescription[]>({
    queryKey: [QK_SCAN_MINI_PRESCRIPTION_LIST, patientId],
    // queryFn: () => prescriptionControllerApi.get(patientId!).then((res) => res.data.reverse()),
    queryFn: () => [],
    enabled: !!patientId,
    gcTime: 0,
  });
};

export const useDeletePrescription = () => {
  const client = useQueryClient();
  return useMutation<Prescription, AxiosError, { prescriptionId: string }>({
    mutationFn: ({ prescriptionId }) => prescriptionControllerApi._delete(prescriptionId).then((res) => res.data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_SCAN_MINI_PRESCRIPTION_LIST] });
    },
  });
};

/**
 * // TODO: SCAN MINI API 배포 전이라 임시 처리
 * 환자 웹 에서 사용되는 API
 * @returns
 */
export const useFetchMyPrescriptions = () => {
  return useQuery<Prescription[]>({
    queryKey: [QK_SCAN_MINI_MY_PRESCRIPTION_LIST],
    // queryFn: () => prescriptionControllerApi.getMyPrescriptions().then((res) => res.data),
    queryFn: () => [],
  });
};

const useFetchAnalysisFiles = (prescriptionId: string, round: number, lang: string) => {
  return useQuery<AnalysisResultDownloadUrls, AxiosError>({
    queryKey: [QK_SCAN_MINI_ANALYSIS_FILES, prescriptionId, round, lang],
    queryFn: () =>
      analysisControllerApi.getAnalysisResultDownloadUrls(prescriptionId, round, lang).then((res) => res.data),
  });
};

/**
 * analysisFiles에서 result.json을 fetch하여 파싱하고, result.png URL을 반환합니다.
 * gzip 압축 없이 일반 JSON/PNG 파일입니다.
 */
export const useFetchAnalysisResult = (prescriptionId: string, round: number, lang: 'ko' | 'en') => {
  const { data: analysisFiles } = useFetchAnalysisFiles(prescriptionId, round, lang);

  const resultJsonPath = analysisFiles?.urls?.['result.json'];
  const resultPngPath = analysisFiles?.urls?.[`raw_${lang === 'ko' ? 'kr' : 'en'}.png`];
  console.log(resultJsonPath, resultPngPath);
  const { data: resultData } = useQuery({
    queryKey: [QK_SCAN_MINI_ANALYSIS_RESULT, resultJsonPath],
    queryFn: async () => {
      if (!resultJsonPath) return null;
      const res = await fetch(resultJsonPath);
      return res.json();
    },
    enabled: !!resultJsonPath,
  });

  return {
    data: resultData ?? {},
    plotImageUrl: resultPngPath,
    isLoading: !analysisFiles || (!!resultJsonPath && resultData === undefined),
  };
};
